import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import AuthPage from "@/pages/AuthPage";
import Layout from "@/components/Layout";
import FeedPage from "@/pages/FeedPage";
import ForYouPage from "@/pages/ForYouPage";
import RelationshipMemoryPage from "@/pages/RelationshipMemoryPage";
import AssistantPage from "@/pages/AssistantPage";
import CreatorStudioPage from "@/pages/CreatorStudioPage";
import CreatorDirectoryPage from "@/pages/CreatorDirectoryPage";
import OutcomesPage from "@/pages/OutcomesPage";
import TrendingPage from "@/pages/TrendingPage";
import DiscoverPage from "@/pages/DiscoverPage";
import CommunitiesPage from "@/pages/CommunitiesPage";
import CommunityDetailPage from "@/pages/CommunityDetailPage";
import MessagesPage from "@/pages/MessagesPage";
import WellnessPage from "@/pages/WellnessPage";
import ProfilePage from "@/pages/ProfilePage";
import LandingPage from "@/pages/LandingPage";
import PremiumPage from "@/pages/PremiumPage";
import JournalPage from "@/pages/JournalPage";
import WeeklyReportPage from "@/pages/WeeklyReportPage";
import SettingsPage from "@/pages/SettingsPage";
import LegalPage from "@/pages/LegalPage";
import PrivacyPage from "@/pages/PrivacyPage";
import SupportPage from "@/pages/SupportPage";

const Private = ({ children }) => {
  const { user, loading } = useAuth();
  React.useEffect(() => {
    if (user) {
      import("@/lib/push").then((module) => module.initPushNotifications()).catch(() => {});
    }
  }, [user]);
  if (loading) return <div className="p-10 font-display text-2xl">Loading…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/feed" replace />;
  return children;
};

const FeatureGate = ({ flag, children }) => {
  const [state, setState] = React.useState("loading");
  React.useEffect(() => {
    let active = true;
    api.get("/features")
      .then((r) => { if (active) setState(r.data?.[flag] ? "enabled" : "disabled"); })
      .catch(() => { if (active) setState("disabled"); });
    return () => { active = false; };
  }, [flag]);

  if (state === "loading") return <div className="brutal-card bg-card p-5">Loading…</div>;
  if (state !== "enabled") return <Navigate to="/feed" replace />;
  return children;
};

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
            <Route path="/legal/:doc" element={<LegalPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/auth" element={<PublicOnly><AuthPage /></PublicOnly>} />
            <Route element={<Private><Layout /></Private>}>
              <Route path="/for-you" element={<FeatureGate flag="haeven4_for_you"><ForYouPage /></FeatureGate>} />
              <Route path="/memory" element={<FeatureGate flag="haeven4_for_you"><RelationshipMemoryPage /></FeatureGate>} />
              <Route path="/assistant" element={<FeatureGate flag="haeven4_companion"><AssistantPage /></FeatureGate>} />
              <Route path="/creator-studio" element={<FeatureGate flag="haeven4_creator_tools"><CreatorStudioPage /></FeatureGate>} />
              <Route path="/directory" element={<FeatureGate flag="haeven4_directory"><CreatorDirectoryPage /></FeatureGate>} />
              <Route path="/outcomes" element={<FeatureGate flag="haeven4_outcomes"><OutcomesPage /></FeatureGate>} />
              <Route path="/feed" element={<FeedPage />} />
              <Route path="/trending" element={<TrendingPage />} />
              <Route path="/discover" element={<DiscoverPage />} />
              <Route path="/communities" element={<CommunitiesPage />} />
              <Route path="/communities/:id" element={<CommunityDetailPage />} />
              <Route path="/messages" element={<MessagesPage />} />
              <Route path="/messages/:userId" element={<MessagesPage />} />
              <Route path="/wellness" element={<WellnessPage />} />
              <Route path="/premium" element={<PremiumPage />} />
              <Route path="/journal" element={<JournalPage />} />
              <Route path="/weekly-report" element={<WeeklyReportPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/profile/:userId" element={<ProfilePage />} />
              <Route path="/settings" element={<SettingsPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
